/**
 * OFFSET of a POLYLINE VERTEX RING — the ONE truth both polyline families use.
 *
 * A planar polyline is a chain of SPANS, and a span is a straight chord when
 * its group-42 bulge is zero and a CIRCULAR ARC when it is not (Autodesk DXF
 * reference, LWPOLYLINE group 42; `ezdxf.math.bulge_to_arc`). AutoCAD's OFFSET
 * builds the parallel curve SPAN BY SPAN in that same alphabet — a line span
 * offsets to a parallel line, an arc span offsets to a CONCENTRIC arc (same
 * centre, radius ± the distance) — and then re-joins the neighbours by
 * intersecting the offset spans. This module does exactly that and returns the
 * result back in the same alphabet: one point per source vertex, each carrying
 * the bulge of the span that leaves it.
 *
 * ## Why the bulge cannot simply be carried across
 *
 * @codex PR #785 round 16, FINDING 1 ("Offset bulged heavy polylines as arcs").
 * Until this module existed, BOTH families sent every span — bulged or not —
 * through the straight-chord ring and copied group 42 across untouched. For
 * the finding's own fixture (an OPEN bulge-(−1) semicircle from `(0,0)` to
 * `(10,0)`, offset 2) that TRANSLATED the chord to `(0,-2) → (10,-2)` while
 * keeping the group 42 — a radius-5 semicircle moved bodily 2 units, which is
 * not the offset of anything. ORIENTATION (r17 P1): a POSITIVE group 42 is the
 * COUNTER-CLOCKWISE sweep (Autodesk DXF group 42: negative iff CW start→end),
 * which puts the arc centre to the LEFT of the chord and the arc itself to the
 * RIGHT of it — so over this west→east chord the UPPER half-circle of radius 5
 * about `(5,0)` is `bulge = −1`. Its offset keeps that centre and changes the
 * RADIUS: `(-2,0) → (12,0)` at radius 7 when the pick point is ABOVE the arc,
 * `(2,0) → (8,0)` at radius 3 when it is below.
 *
 * This defect was NOT introduced by the heavy-POLYLINE arm: `git show
 * origin/main` has the pre-extraction LWPOLYLINE case spreading `...verts[i]`
 * over a chord-only offset, so LWPOLYLINE has produced the wrong curve for a
 * bulged span for as long as OFFSET has existed here. The round-14 extraction
 * merely gave the SECOND family the same wrong answer, which is why the repair
 * lands once, here, and both families receive it together.
 *
 * ## The three rules this implements
 *
 * 1. **Offset a span in its own alphabet.** Line → {@link offsetLineSegment}
 *    (unchanged, so an all-straight polyline's result is byte-identical to the
 *    pre-fix one). Arc → CONCENTRIC: the centre stays, the radius moves by the
 *    offset distance, and each endpoint slides along its own radius. Which WAY
 *    it moves is the same LOCAL side test the straight arm makes — the sign of
 *    the pick point's radial component at the arc's own midpoint — and NOT the
 *    whole-circle "is the pick point inside?" rule a standalone `Circle` /
 *    `Arc` uses; see {@link buildOffsetSpan} for the stadium that separates
 *    the two.
 * 2. **Join neighbours by intersecting the offset spans.** Line × line is
 *    {@link lineLineIntersection} — the same closed form, same operand order,
 *    as the helper this replaced, so no corner of a straight polyline moves by
 *    one ULP. Line × arc is {@link lineCircleIntersection} and arc × arc is
 *    {@link circleCircleIntersection}, each resolved to the candidate that
 *    modifies the two spans LEAST ({@link bestJoin}) — the join that leaves
 *    the neighbouring extents most nearly intact, which is what a miter is.
 * 3. **Re-derive the bulge from the arc that survives.** A span whose two ends
 *    were NOT trimmed (an open chain's first and last, and any join with no
 *    intersection) keeps its source bulge EXACTLY — a radial scaling preserves
 *    the swept angle, so the correct new bulge is bit-for-bit the old one and
 *    recomputing it would only inject `atan2` round-off. A span that WAS
 *    trimmed sweeps a different angle about the same centre, so its bulge is
 *    recomputed from the new endpoints: `bulge = sign · tan(sweep / 4)`.
 *
 * ## Open chains keep BOTH of their own endpoints
 *
 * @codex PR #785 round 15, P2 ("Preserve the first endpoint of open polyline
 * offsets"). An interior vertex of an open chain is shared by two spans and is
 * therefore a MITER — the intersection of the two offset spans. Its first and
 * last vertices are not: they belong to one span each, so they offset
 * PERPENDICULARLY (radially, for an arc span), which is what AutoCAD's OFFSET
 * produces for an open polyline and what `ezdxf.math.offset_vertices_2d`'s own
 * `if not closed:` arm yields (verified against ezdxf 1.4.4 — that function
 * builds one offset segment per span and yields `offset_segments[0][0]`, the
 * first offset span's own START, before any intersection, while the CLOSED arm
 * does `offset_segments.insert(0, offset_segments[-1])`, exactly the
 * wrap-around below).
 *
 * ## Why one helper and not two arms
 *
 * @codex PR #785 round 14, "Support 2D heavy polylines in OFFSET": a heavy 2D
 * `POLYLINE`'s planar geometry IS vertices plus per-vertex bulges, i.e.
 * `LWPOLYLINE`'s geometry, so the faithful implementation is to PROJECT the
 * heavy subtype onto this one ring rather than write a second offset
 * algorithm. Both `geometryReducerEdit.ts` arms call this function on the same
 * `{x, y, bulge}` list, so the same curve produces bit-identical offset output
 * in either family — pinned as a metamorphic property in
 * `apps/web/…/__tests__/geometryReducerEdit.offsetHeavyPolyline.metamorphic.test.ts`.
 */

import {
  bulgeArc,
  bulgeArcPointAt,
  type BulgePoint,
} from "@dxf-ai-editor/drawing-model";

import {
  lineCircleIntersection,
  lineLineIntersection,
  offsetLineSegment,
} from "./geometryTransforms";
import { circleCircleIntersection } from "./trimExtendGeometryCore";
import type { Point2D } from "./affine2dProbe";

/** One source vertex of the ring: a plan position plus the bulge of the span leaving it. */
export interface OffsetRingVertex {
  readonly x: number;
  readonly y: number;
  /** DXF group 42. Absent is read as 0 (a straight span), never materialised back. */
  readonly bulge?: number;
}

/**
 * One offset vertex.
 *
 * `bulge` is PRESENCE-PRESERVING on purpose: `undefined` means "this span's
 * group 42 is unchanged — leave the source record's field exactly as it was,
 * present or absent". Only a span whose sweep actually changed carries a
 * number. That is what keeps an all-straight polyline's offset byte-identical
 * to the pre-fix output (heavy `POLYLINE` vertices schema group 42 is
 * `.optional()`, so writing a literal 0 would materialise a field the source
 * never had).
 */
export interface OffsetRingPoint {
  readonly x: number;
  readonly y: number;
  readonly bulge?: number;
}

/**
 * The smallest radius an inward-offset circular span may collapse to.
 *
 * Deliberately the same `1e-6` {@link offsetCircle} floors a standalone
 * `Circle` / `Arc` at, so "the offset distance is bigger than the radius"
 * has ONE answer across every circular thing OFFSET can be pointed at. It is
 * restated rather than imported because that helper lives in a module this one
 * already imports, and importing the constant back would close a cycle.
 */
const OFFSET_ARC_MIN_RADIUS = 1e-6;

/**
 * An offset span, in the alphabet the source span was written in.
 *
 * Exported alongside {@link spanOvershoot} — see that function's own note on
 * why its value is asserted directly.
 */
export type OffsetSpan =
  | { readonly kind: "line"; readonly start: Point2D; readonly end: Point2D }
  | {
      readonly kind: "arc";
      readonly start: Point2D;
      readonly end: Point2D;
      readonly center: Point2D;
      readonly radius: number;
      /** `+1` when the source bulge was `>= 0` — {@link bulgeArc}'s own rule. */
      readonly bulgeSign: 1 | -1;
      /** Negative ⇒ the arc runs CLOCKWISE about `center` (a POSITIVE bulge). */
      readonly signedSweep: number;
    };

/**
 * `a` reduced into `[0, 2π)`.
 *
 * The double-modulo idiom rather than `m < 0 ? m + 2π : m`: the branching
 * form has to decide what to do at EXACTLY zero, where `<` and `<=` disagree
 * (0 versus a full turn) on an input no caller can be shown to avoid, and that
 * is a decision with no observable consequence to defend. `((a % T) + T) % T`
 * maps 0 to 0 by construction and has no boundary to argue about.
 */
function normalizeAngle(a: number): number {
  // `2 * Math.PI` spelled at the use site rather than hoisted to a module
  // const: a module-level initialiser is evaluated when the file LOADS, before
  // any per-test mutant switch is active, so such a constant is a mutation
  // blind spot (Stryker's "static mutant") — its arithmetic can be corrupted
  // and no test can observe it. Inline, the same arithmetic runs per call and
  // is gated like everything else here.
  const twoPi = 2 * Math.PI;
  return ((a % twoPi) + twoPi) % twoPi;
}

/**
 * The offset image of ONE span, in the same alphabet the source span used.
 *
 * A zero (or absent) bulge, a zero-length chord and a bulge below
 * {@link bulgeArc}'s own straightness floor all resolve to `null` there, which
 * is precisely the set of spans that are straight edges — so the line arm is
 * reached on exactly the inputs the pre-fix code handled, and by the very same
 * call.
 */
function buildOffsetSpan(
  from: OffsetRingVertex,
  to: OffsetRingVertex,
  distance: number,
  sidePoint: Point2D,
): OffsetSpan {
  const bulge = from.bulge ?? 0;
  const arc = bulgeArc(
    { x: from.x, y: from.y } satisfies BulgePoint,
    { x: to.x, y: to.y } satisfies BulgePoint,
    bulge,
  );
  if (!arc) {
    const seg = offsetLineSegment(
      { x: from.x, y: from.y },
      { x: to.x, y: to.y },
      distance,
      sidePoint,
    );
    return { kind: "line", start: seg.start, end: seg.end };
  }
  const center = { x: arc.cx, y: arc.cy };
  // WHICH SIDE — the LOCAL test, the exact analogue of the one
  // {@link offsetLineSegment} runs for a straight span, and NOT the
  // whole-circle "is the pick point inside?" rule {@link offsetCircle} uses.
  //
  // That rule is right for a STANDALONE `Circle` / `Arc`, where inside and
  // outside are the only two sides there are. It is wrong for a SPAN of a
  // polyline, whose side must agree with its straight neighbours': take a
  // stadium 40 long with half-circle caps of radius 10 and pick its centre
  // (20, 10) to offset inwards. The straight sides move in, but the pick point
  // is 20 from the cap's centre and so OUTSIDE its circle, and the whole-circle
  // rule would grow that cap OUTWARDS — the two ends of the same offset curve
  // walking in opposite directions.
  //
  // The straight-span test is "is the pick point on the +normal side of the
  // span's MIDPOINT?", and an arc's normal at its midpoint is RADIAL. So the
  // side is the sign of the pick point's RADIAL component about the arc's own
  // midpoint, which drops exactly the tangential part the whole-circle test
  // wrongly counted. `bulgeArcPointAt` is `drawing-model`'s one-truth arc
  // parameterisation (u = 0.5 ⇒ the arc's own midpoint), not a re-derivation.
  const mid = bulgeArcPointAt(arc, 0.5);
  const nx = (mid.x - center.x) / arc.radius;
  const ny = (mid.y - center.y) / arc.radius;
  const grow =
    (sidePoint.x - mid.x) * nx + (sidePoint.y - mid.y) * ny >= 0;
  // …and the RADIUS moves by the offset distance, in or out, floored rather
  // than taken through zero — the SAME stated decision {@link offsetCircle}
  // makes for a standalone Circle / Arc, down to the floor value, which is
  // restated here rather than imported only because that helper lives in the
  // module THIS one imports (`./geometryTransforms`) and reaching back for the
  // constant would close a dependency cycle. A span whose radius the distance
  // would consume therefore collapses to a cusp of that radius. AutoCAD
  // instead DROPS such a span and re-joins its neighbours, which needs the
  // self-intersection pass this ring deliberately does not have — a documented
  // known-incomplete subsystem (CLAUDE.md ПРИОРИТЕТ #1's one allowed
  // exception), visible on screen as a collapsed arc rather than silent.
  //
  // The SIGNED CHANGE is computed directly, never as `newRadius − radius`:
  // that difference CANCELS catastrophically on a large radius (a 1000-long
  // chord with a group-42 of 7.6e-6 has R ≈ 3.3e7, whose ULP is 3.7e-9, so the
  // recovered "distance" would be wrong in the ninth decimal — an error at the
  // scale `COINCIDENT_EPS` itself lives at).
  const shrunk = arc.radius - distance;
  // EQUIVALENT MUTANT: EqualityOperator, `>=` -> `>`. The two arms AGREE at
  // the only value where the operators disagree: when `shrunk` is EXACTLY the
  // floor, `-distance` gives a new radius of `arc.radius - distance`, which IS
  // `shrunk`, which IS the floor — the same number the other arm writes. No
  // input can distinguish them. (Hoisted to its own line so the suppression
  // below sits directly on the mutated comparison rather than on the head of
  // a multi-line ternary, where Stryker does not match it.)
  // Stryker disable next-line EqualityOperator: at shrunk === the floor both arms produce the floor (see above)
  const survivesTheShrink = shrunk >= OFFSET_ARC_MIN_RADIUS;
  const delta = grow
    ? distance
    : survivesTheShrink
      ? -distance
      : OFFSET_ARC_MIN_RADIUS - arc.radius;
  const radius = arc.radius + delta;
  // Each endpoint slides along its OWN radius by that change: `P' = P + n·Δ`,
  // algebraically the same point as the radial scaling `C + (P − C)·R'/R` but
  // far better CONDITIONED — the scaling form takes a near-unit multiple of a
  // large vector and then subtracts, so its absolute error grows with R and
  // swamps the offset itself on a near-straight span, while this form's unit
  // normal carries only RELATIVE round-off.
  const fromNx = (from.x - center.x) / arc.radius;
  const fromNy = (from.y - center.y) / arc.radius;
  const toNx = (to.x - center.x) / arc.radius;
  const toNy = (to.y - center.y) / arc.radius;
  return {
    // EQUIVALENT MUTANT: StringLiteral, `"arc"` -> `""`. This tag is only ever
    // TESTED for `"line"` — `spanOvershoot` and `joinOffsetSpans` both branch
    // on `kind === "line"` and treat everything else as the arc case — so any
    // non-`"line"` value behaves identically. The literal is a discriminant
    // TypeScript needs, not a value this module reads.
    // Stryker disable next-line StringLiteral: only `kind === "line"` is ever tested (see above)
    kind: "arc",
    start: { x: from.x + fromNx * delta, y: from.y + fromNy * delta },
    end: { x: to.x + toNx * delta, y: to.y + toNy * delta },
    center,
    radius,
    // EQUIVALENT MUTANT: EqualityOperator, `>=` -> `>`. The two disagree only
    // at `bulge === 0` (and `-0`), and this line is UNREACHABLE there:
    // `bulgeArc` answers `null` below its own straightness floor of 1e-12, so
    // a zero bulge has already taken the line arm above.
    // Stryker disable next-line EqualityOperator: bulge === 0 takes the line arm above (see above)
    bulgeSign: bulge >= 0 ? 1 : -1,
    signedSweep: arc.signedSweep,
  };
}

/**
 * How far OUTSIDE its own extent `point` lies on `span`, as a LENGTH along the
 * span (0 when it lies within it).
 *
 * This is the cost of using `point` as a join: a corner that falls inside both
 * offset spans TRIMS them, one that falls beyond an end EXTENDS that span, and
 * an offset corner should modify the two curves as little as possible — the
 * "minimum modification" rule a miter is. For a line it is the distance past
 * whichever end was overshot; for an arc it is the SHORTER of the two arc
 * distances from the point to the sweep, so a candidate sitting just behind
 * the start is charged the short way round rather than a near-full turn.
 *
 * ## Exported for its own tests, not for callers
 *
 * {@link offsetPolylineVertexRing} is this module's only production consumer
 * and the only one there should be. The export exists because the function's
 * VALUE, not merely the ORDER it induces on two candidates, is a contract:
 * many distortions of it (scaling either arm, swapping `min` for `max`,
 * dropping the radius factor) leave the order intact on the handful of shapes
 * an end-to-end fixture can spell, so the value has to be asserted directly or
 * it is not asserted at all.
 */
export function spanOvershoot(span: OffsetSpan, point: Point2D): number {
  if (span.kind === "line") {
    const dx = span.end.x - span.start.x;
    const dy = span.end.y - span.start.y;
    const lenSq = dx * dx + dy * dy;
    if (lenSq === 0) return Math.hypot(point.x - span.start.x, point.y - span.start.y);
    const t =
      ((point.x - span.start.x) * dx + (point.y - span.start.y) * dy) / lenSq;
    const len = Math.sqrt(lenSq);
    if (t < 0) return -t * len;
    // EQUIVALENT MUTANT: EqualityOperator, `>` -> `>=`. At `t === 1` exactly
    // the point is ON the span's end: this arm returns `(1 - 1) * len === 0`
    // and the fall-through returns `0`. The same number either way.
    // Stryker disable next-line EqualityOperator: at t === 1 both this arm and the fall-through return 0
    if (t > 1) return (t - 1) * len;
    return 0;
  }
  // A radial scaling is a similarity about the centre, so the OFFSET arc sweeps
  // the same angle the source span did — `signedSweep` is still its extent.
  const startAngle = Math.atan2(
    span.start.y - span.center.y,
    span.start.x - span.center.x,
  );
  const angle = Math.atan2(point.y - span.center.y, point.x - span.center.x);
  const sweep = Math.abs(span.signedSweep);
  // EQUIVALENT MUTANT: EqualityOperator, `>=` -> `>`. `signedSweep` is
  // `bulgeArc`'s `bulgeSign * |theta|` with `|theta| > 0` for every span that
  // reached the arc arm, so it is NEVER 0 — the only value at which the two
  // operators disagree. (The r17 P1 orientation fix changed that expression's
  // SIGN, not its magnitude or its never-zero-ness, so the suppression stands.)
  // Stryker disable next-line EqualityOperator: an arc span's signedSweep is never 0 (see above)
  const runsCcw = span.signedSweep >= 0;
  const u = runsCcw
    ? normalizeAngle(angle - startAngle)
    : normalizeAngle(startAngle - angle);
  // EQUIVALENT MUTANT: EqualityOperator, `<=` -> `<`. At `u === sweep` the
  // point is ON the span's far end: this returns 0, and the line below returns
  // `Math.min(u - sweep, ...) * radius`, whose first term is then exactly 0.
  // Stryker disable next-line EqualityOperator: at u === sweep both this return and the next line give 0
  if (u <= sweep) return 0;
  return Math.min(u - sweep, 2 * Math.PI - u) * span.radius;
}

/**
 * The candidate that modifies the two spans LEAST; `null` for an empty
 * candidate set.
 *
 * The cost is {@link spanOvershoot} summed over both spans, which is what picks
 * the join on the side the curve actually bows to. A plain nearest-point rule
 * cannot: for a symmetric figure — a closed polyline with one semicircular
 * side, say — the two circle/line crossings are EXACTLY equidistant from every
 * image of the shared vertex, and only "which one leaves the spans' own extents
 * intact" separates them.
 *
 * An exact COST tie is resolved by candidate ORDER, i.e. the first minimum
 * wins. Deliberately no distance tie-break: a tie means both candidates lie
 * within both spans, so neither trims more than the other and the two are
 * equally good joins — and the configurations that produce one are symmetric,
 * which makes every distance to every image of the shared vertex tie as well
 * (a second rule that decides nothing is a second rule no test can hold to
 * account). The closed-form helpers return their roots in a fixed order, so
 * this is deterministic and identical in both polyline families, which is what
 * the families' bit-identity property needs.
 */
function bestJoin(
  candidates: readonly Point2D[],
  prev: OffsetSpan,
  next: OffsetSpan,
): Point2D | null {
  let best: Point2D | null = null;
  let bestCost = Infinity;
  for (const c of candidates) {
    const cost = spanOvershoot(prev, c) + spanOvershoot(next, c);
    if (cost < bestCost) {
      bestCost = cost;
      best = c;
    }
  }
  return best;
}

/**
 * Where two adjacent offset spans meet, or `null` when they do not meet at all
 * (parallel lines, concentric or separated circles, a line that misses the
 * circle). The caller falls back to the later span's own start — the
 * perpendicular/radial offset of the shared source vertex — exactly as the
 * straight-only predecessor did for its parallel case.
 */
function joinOffsetSpans(
  prev: OffsetSpan,
  next: OffsetSpan,
): Point2D | null {
  // Nested on ONE kind at a time rather than four compound `a && b` guards:
  // each test below is genuinely two-way, and the last pair needs no test at
  // all. A fourth `prev.kind === "arc" && next.kind === "arc"` guard would be
  // provably always true by then, i.e. a condition that can be neither wrong
  // nor tested — and the nesting is also what narrows `prev`/`next` for the
  // compiler without an assertion.
  if (prev.kind === "line") {
    if (next.kind === "line") {
      // The straight join, byte-for-byte the closed form this module replaced
      // — one solution or none, so no candidate selection runs and an
      // all-straight polyline's every corner is bit-identical to the pre-fix
      // output.
      return lineLineIntersection(prev.start, prev.end, next.start, next.end);
    }
    return bestJoin(
      lineCircleIntersection(prev.start, prev.end, next.center, next.radius),
      prev,
      next,
    );
  }
  if (next.kind === "line") {
    return bestJoin(
      lineCircleIntersection(next.start, next.end, prev.center, prev.radius),
      prev,
      next,
    );
  }
  return bestJoin(
    circleCircleIntersection(
      prev.center,
      prev.radius,
      next.center,
      next.radius,
    ),
    prev,
    next,
  );
}

/**
 * The group-42 bulge of `span` once its ends sit at `start` / `end`.
 *
 * `undefined` = leave the source's field alone. That is returned for every
 * straight span (there is no arc to re-derive) and for an arc span NEITHER of
 * whose ends was trimmed: a radial scaling is a similarity about the centre,
 * so it preserves the swept angle exactly and the correct answer is the source
 * number itself, bit for bit.
 *
 * Otherwise the arc still lies on its own offset circle — every join above is
 * computed ON that circle — so the new sweep is just the angular distance from
 * `start` to `end` taken in the span's own rotational direction, and
 * `bulge = tan(θ/4)` inverts {@link bulgeArc}'s `θ = 4·atan(bulge)`. A sweep
 * that a trim has pushed to (or through) a full turn has no finite bulge —
 * one group-42 value cannot name a closed circle — so the source value is kept
 * rather than writing a non-finite number into the document.
 */
function offsetSpanBulge(
  span: OffsetSpan,
  start: Point2D,
  end: Point2D,
  trimmed: boolean,
): number | undefined {
  if (span.kind === "line" || !trimmed) return undefined;
  const startAngle = Math.atan2(start.y - span.center.y, start.x - span.center.x);
  const endAngle = Math.atan2(end.y - span.center.y, end.x - span.center.x);
  // EQUIVALENT MUTANT: EqualityOperator, `<` -> `<=`. The same argument
  // `spanOvershoot` makes above: `signedSweep` is never 0 for a span that
  // reached the arc arm, and 0 is the only value at which the two operators
  // disagree.
  // Stryker disable next-line EqualityOperator: an arc span's signedSweep is never 0 (see spanOvershoot)
  const runsCw = span.signedSweep < 0;
  const sweep = runsCw
    ? normalizeAngle(startAngle - endAngle)
    : normalizeAngle(endAngle - startAngle);
  const bulge = span.bulgeSign * Math.tan(sweep / 4);
  return Number.isFinite(bulge) ? bulge : undefined;
}

/**
 * Offset every span of `points` by `distance` towards `sidePoint` and rebuild
 * the ring.
 *
 * Returns exactly `points.length` points: a CLOSED ring contributes one offset
 * span per vertex and so one corner per vertex, while an OPEN chain contributes
 * `n - 1` spans, and its `n` points are the FIRST span's own start, the `n - 2`
 * interior joins, and the LAST span's own end.
 *
 * PRECONDITION: `points.length >= 2`, enforced at BOTH call sites (the
 * LWPOLYLINE arm's own `vertices.length < 2` guard, and
 * `heavyPolylineDrawnChain`'s `< 2 indices` floor). Below two points an open
 * chain has no span at all and the closing `spans[spans.length - 1]` read would
 * be out of range.
 */
export function offsetPolylineVertexRing(
  points: readonly OffsetRingVertex[],
  closed: boolean,
  distance: number,
  sidePoint: Point2D,
): OffsetRingPoint[] {
  const segCount = closed ? points.length : points.length - 1;
  const spans: OffsetSpan[] = [];
  for (let i = 0; i < segCount; i++) {
    const j = (i + 1) % points.length;
    spans.push(buildOffsetSpan(points[i], points[j], distance, sidePoint));
  }

  // Corner k is where span k-1 meets span k. An OPEN chain has NO span before
  // its first, so corner 0 is span 0's own start; a CLOSED ring genuinely HAS
  // that predecessor (its closing span), so the wrap-around applies there and
  // is deliberately untouched — `closed` is the whole difference between the
  // two shapes (@codex PR #785 round 15, P2).
  const corners: Point2D[] = [];
  const trimmed: boolean[] = [];
  for (let i = 0; i < segCount; i++) {
    const span = spans[i];
    if (!closed && i === 0) {
      corners.push({ x: span.start.x, y: span.start.y });
      trimmed.push(false);
      continue;
    }
    const prev = spans[(i - 1 + spans.length) % spans.length];
    const hit = joinOffsetSpans(prev, span);
    corners.push(hit ?? { x: span.start.x, y: span.start.y });
    trimmed.push(hit !== null);
  }
  // An open chain's last point is the last span's own end — its second
  // untrimmed endpoint.
  if (!closed) {
    const last = spans[spans.length - 1];
    corners.push({ x: last.end.x, y: last.end.y });
    trimmed.push(false);
  }

  return corners.map((corner, i) => {
    const span = spans[i];
    // The LAST vertex of an OPEN chain leaves no span: its group 42 is inert
    // DXF payload, so it is carried verbatim like every other per-vertex
    // channel the caller spreads.
    if (!span) return { x: corner.x, y: corner.y };
    const endIndex = (i + 1) % corners.length;
    const bulge = offsetSpanBulge(
      span,
      corner,
      corners[endIndex],
      trimmed[i] || trimmed[endIndex],
    );
    return bulge === undefined
      ? { x: corner.x, y: corner.y }
      : { x: corner.x, y: corner.y, bulge };
  });
}
